// src/components/Disciplinary.jsx


import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import moment from "moment";
import Card from "./card";
import DisciplinaryCharts from "./Charts";
import AddDisciplinaryCaseForm from "./AddDisciplinaryCaseForm";
import { FaFileAlt, FaCheckCircle, FaEdit, FaHourglassHalf } from "react-icons/fa";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const Disciplinary = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingCase, setEditingCase] = useState(null);

  // Search, pagination, export
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const token = localStorage.getItem("token");

    // Helper for Status Pills
  const getStatusBadge = (status) => {
    const baseClass = "px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ";
    switch (status?.toLowerCase()) {
      case 'resolved':
        return <span className={baseClass + "bg-green-100 text-green-800 border border-green-200"}>Resolved</span>;
      case 'dismissed':
        return <span className={baseClass + "bg-red-100 text-red-800 border border-red-200"}>Dismissed</span>;
      case 'pending':
      default:
        return <span className={baseClass + "bg-yellow-100 text-yellow-800 border border-yellow-200"}>Pending</span>;
    }
  };

  const fetchCases = async () => {
    try {
      const res = await axios.get(
        "https://ims.holdthechild.net/api/disciplinary-cases?limit=1000",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCases(res.data.results || []);
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Unauthorized. Please log in again."
          : "Failed to fetch disciplinary cases"
      );
      toast.error("Error loading cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError("Unauthorized. Please log in.");
      setLoading(false);
      return;
    }
    fetchCases();
  }, [token]);

  // Close export dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter cases by search
  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) => {
      return (
        (c.staffName && c.staffName.toLowerCase().includes(q)) ||
        (c.offense && c.offense.toLowerCase().includes(q)) ||
        (c.status && c.status.toLowerCase().includes(q))
      );
    });
  }, [cases, search]);

  useEffect(() => setCurrentPage(1), [search, cases]);

  // Paginated data
  const paginatedCases = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCases.slice(start, start + itemsPerPage);
  }, [filteredCases, currentPage]);

  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);

  const totalCases = cases.length;
  const resolvedCases = cases.filter((c) => c.status?.toLowerCase() === "resolved").length;
  const pendingCases = cases.filter((c) => c.status?.toLowerCase() === "pending").length;

  // Export mapping
  const mapCaseToExportRow = (c) => ({
    "Staff Name": c.staffName || "N/A",
    "Reported By": c.reportedBy?.firstName + ' ' + c.reportedBy?.lastName || "N/A",
    "Resolved By": c.resolvedByFullName || "N/A",
    Department: c.department || "N/A",
    Offense: c.offense || "N/A",
    "Action Taken": c.actionTaken || "N/A",
    "Reported Date": c.dateReported ? moment(c.dateReported).format("YYYY-MM-DD") : "N/A",
    "Last Action Date": c.updatedAt ? moment(c.updatedAt).format("YYYY-MM-DD HH:mm") : "N/A",
    Status: c.status || "N/A",
  });

  const exportToExcel = () => {
    if (!filteredCases.length) return alert("No disciplinary cases to export!");
    const exportData = filteredCases.map(mapCaseToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Disciplinary Cases");
    worksheet["!cols"] = Object.keys(exportData[0]).map((col) => ({
      wch: Math.max(col.length, ...exportData.map((row) => (row[col] ? row[col].toString().length : 0))) + 5,
    }));
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "disciplinary_cases_export.xlsx");
  };

  const exportToCSV = () => {
    if (!filteredCases.length) return alert("No disciplinary cases to export!");
    const exportData = filteredCases.map(mapCaseToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    saveAs(new Blob([XLSX.utils.sheet_to_csv(worksheet)], { type: "text/csv;charset=utf-8;" }), "disciplinary_cases_export.csv");
  };

  const exportToPDF = () => {
    if (!filteredCases.length) return alert("No disciplinary cases to export!");
    const exportData = filteredCases.map(mapCaseToExportRow);
    const doc = new jsPDF("l", "pt", "a4");
    const columns = Object.keys(exportData[0]);

    doc.setFontSize(18);
    doc.setTextColor(237, 125, 49);
    doc.text("Disciplinary Cases Export", 40, 40);

    autoTable(doc, {
      startY: 60,
      head: [columns],
      body: exportData.map((row) => columns.map((col) => row[col])),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [237, 125, 49] },
      theme: "striped",
      margin: { left: 20, right: 20 },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
          data.settings.margin.left, doc.internal.pageSize.height - 10
        );
      },
    });
    doc.save("disciplinary_cases_export.pdf");
  };

  const handleExport = (format) => {
    setExportDropdownOpen(false);
    if (format === "excel") exportToExcel();
    else if (format === "csv") exportToCSV();
    else if (format === "pdf") exportToPDF();
    else alert("Unsupported export format");
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Disciplinary Dashboard</h1>
        <button
          className="bg-orange-500 text-white px-4 py-2 rounded shadow hover:bg-orange-600"
          onClick={() => { setEditingCase(null); setShowDrawer(true); }}
        >
          Add Disciplinary Case
        </button>
      </div>

      {/* Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setShowDrawer(false)}></div>
          <div className="bg-white p-6 rounded-lg shadow-xl relative max-w-md w-full max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
              onClick={() => setShowDrawer(false)}
            >
              <X size={24} />
            </button>
            <AddDisciplinaryCaseForm
              onClose={() => { setShowDrawer(false); setEditingCase(null); fetchCases(); }}
              caseToEdit={editingCase}
            />
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Total Cases Reported" value={totalCases} icon={<FaFileAlt className="text-orange-500" />} progress={100} />
        <Card title="Cases Resolved" value={resolvedCases} icon={<FaCheckCircle className="text-green-500" />} progress={totalCases ? (resolvedCases / totalCases) * 100 : 0} />
        <Card title="Cases Pending" value={pendingCases} icon={<FaHourglassHalf className="text-yellow-500" />} progress={totalCases ? (pendingCases / totalCases) * 100 : 0} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-orange-50 p-4 rounded-xl text-center shadow">
          <h2 className="text-lg font-semibold mb-2">Cases per Department</h2>
          {!loading && !error && <DisciplinaryCharts cases={cases} type="departments" />}
        </div>
        <div className="bg-orange-50 p-4 rounded-xl text-center shadow">
          <h2 className="text-lg font-semibold mb-2">Offense Type Distribution</h2>
          {!loading && !error && <DisciplinaryCharts cases={cases} type="offenses" />}
        </div>
        <div className="bg-orange-50 p-4 rounded-xl text-center shadow">
          <h2 className="text-lg font-semibold mb-2">Monthly Case Trends</h2>
          {!loading && !error && <DisciplinaryCharts cases={cases} type="monthly" />}
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 bg-white rounded shadow p-4">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Disciplinary Cases Table</h2>

        {/* Search + Export */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <input
            type="search"
            placeholder="Search cases by staff, offense or status"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-label="Search disciplinary cases"
          />

          <div className="relative inline-block text-left" ref={dropdownRef}>
            <button
              onClick={() => setExportDropdownOpen((open) => !open)}
              type="button"
              className="inline-flex justify-center w-full rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-haspopup="true"
              aria-expanded={exportDropdownOpen}
              aria-controls="export-menu"
            >
              Export
              <svg
                className="ml-2 -mr-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {exportDropdownOpen && (
              <div
                id="export-menu"
                className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="export-menu-button"
                tabIndex={-1}
              >
                <div className="py-1">
                  {["excel", "csv", "pdf"].map((format) => (
                    <button
                      key={format}
                      onClick={() => handleExport(format)}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                      role="menuitem"
                    >
                      Export as {format.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          {/* Desktop Table */}
          <table className="hidden md:table min-w-full text-sm">
            <thead>
              <tr className="bg-orange-100 text-gray-600 text-left">
                <th className="px-4 text-orange-600 py-2">Department</th>
                <th className="px-4 text-orange-600 py-2">Staff Name</th>
                <th className="px-4 text-orange-600 py-2">Reported By</th>
                <th className="px-4 text-orange-600 py-2">Offense</th>
                <th className="px-4 text-orange-600 py-2">Action Taken</th>
                <th className="px-4 text-orange-600 py-2">Reported Date</th>
                <th className="px-4 text-orange-600 py-2">Status</th>
                <th className="px-4 text-orange-600 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCases.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-gray-500">
                    No disciplinary cases found.
                  </td>
                </tr>
              ) : (
                paginatedCases.map((row, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{row.staffName || "N/A"}</td>
                    <td className="px-4 py-3 text-gray-600">{row.department || "N/A"}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={row.offense}>{row.offense || "N/A"}</td>
                    <td className="px-4 py-3 text-gray-500 italic max-w-xs truncate" title={row.briefExplanation}>
                        {row.briefExplanation || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                        {row.createdAt ? moment(row.createdAt).format("MMM D, YYYY") : "N/A"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                        {row.updatedAt ? moment(row.updatedAt).format("MMM D, YYYY") : "N/A"}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(row.status)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => { setEditingCase(row); setShowDrawer(true); }}
                        className="inline-flex items-center px-3 py-1.5 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all shadow-sm"
                      >
                        <FaEdit className="mr-1.5 h-3 w-3" />
                        Take Action
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Mobile Card Layout */}
          <div className="space-y-4 md:hidden">
            {paginatedCases.length === 0 ? (
              <p className="text-center py-4 text-gray-500">No disciplinary cases found.</p>
            ) : (
              paginatedCases.map((row, index) => (
                <div key={index} className="border rounded-lg p-4 shadow-sm bg-white space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-bold text-gray-800 block">{row.staffName || "N/A"}</span>
                      <span className="text-xs text-gray-500">{row.department || "N/A"}</span>
                    </div>
                    {getStatusBadge(row.status)}
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold block text-xs text-gray-400 uppercase">Offense</span>
                    {row.offense || "N/A"}
                  </div>

                  <div className="text-sm text-gray-500 italic bg-gray-50 p-2 rounded">
                    <span className="font-semibold block text-xs text-gray-400 uppercase mb-1">Reporter's Note</span>
                    {row.briefExplanation || "No description provided."}
                  </div>

                  <div className="flex justify-between items-center text-xs text-gray-400 pt-2 border-t">
                    <span>Reported: {row.createdAt ? moment(row.createdAt).format("MMM D") : "N/A"}</span>
                    <span>Action: {row.updatedAt ? moment(row.updatedAt).format("MMM D") : "N/A"}</span>
                  </div>

                  <button
                    className="w-full mt-2 bg-orange-500 text-white py-2 rounded-md text-sm font-semibold shadow hover:bg-orange-600 transition-colors flex justify-center items-center"
                    onClick={() => { setEditingCase(row); setShowDrawer(true); }}
                  >
                    <FaEdit className="mr-2 h-4 w-4" />
                    Take Action
                  </button>
                </div>
              ))
            )}
          </div>
        </div>


        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`rounded px-3 py-1 ${currentPage === 1 ? "cursor-not-allowed bg-gray-300" : "bg-orange-500 text-white hover:bg-orange-600"}`}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`rounded px-3 py-1 ${currentPage === i + 1 ? "bg-orange-700 text-white" : "bg-orange-100 text-orange-700 hover:bg-orange-200"}`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`rounded px-3 py-1 ${currentPage === totalPages ? "cursor-not-allowed bg-gray-300" : "bg-orange-500 text-white hover:bg-orange-600"}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Disciplinary;
